import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const endpoint = (tenantUrl: string, path: string) =>
  `${tenantUrl.replace(/\/$/, "")}${path}`;

export const gainsight = defineConnector({
  identifier: "gainsight",
  name: "Gainsight",
  tagLine: "Customer success platform",
  keywords: "customer experience retention health scores csm",
  logo,
  protocol: "http",
  tags: ["data", "productivity"],
  defaultUrlPattern: ({ tenantUrl }) => domainToRegex(tenantUrl),
  auth: oauthAuth,
  steps: [
    {
      title: "Enter your Gainsight tenant URL",
      items: [
        {
          kind: "baseUrl",
          label: "Tenant URL",
          placeholder: "https://your-org.gainsightcloud.com",
          required: true,
          helperText: "The base URL you use to sign in to Gainsight CS.",
          target: { kind: "configuration", field: "tenantUrl" },
        },
      ],
    },
    {
      title: "Create a Gainsight OAuth application",
      items: [
        {
          kind: "markdown",
          content:
            "As a Gainsight super admin, open **Administration › User Management › Authentication › OAuth Applications**, and add an application. Enable PKCE, choose **Read** or **Read/Write**, and add the callback URL below.",
        },
        {
          kind: "copyable",
          label: "Callback URL",
          value: "{API_URL}/connected-apps/oauth/gainsight/callback",
        },
      ],
    },
    {
      title: "Enter your OAuth application details",
      items: [
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          helperText:
            "Gainsight displays the client secret only once when the application is created or the secret is regenerated.",
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
        {
          kind: "select",
          label: "Scope",
          required: true,
          defaultValue: "read_write",
          helperText: "Choose the same scope as the Gainsight application.",
          options: [
            { value: "read_write", label: "Read and write" },
            { value: "read", label: "Read only" },
          ],
          target: { kind: "oauthConnection", field: "scopes" },
        },
      ],
    },
    {
      title: "Authorize your Gainsight account",
      items: [
        {
          kind: "oauth",
          dependsOn: [
            { kind: "configuration", field: "tenantUrl" },
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
            { kind: "oauthConnection", field: "scopes" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: ({ tenantUrl }) =>
      endpoint(tenantUrl, "/v1/authorize"),
    // Gainsight’s docs show a camelCase JSON body for this endpoint, but it parses only standard form-encoded parameters and reports any JSON body as invalid credentials.
    tokenEndpoint: ({ tenantUrl }) =>
      endpoint(tenantUrl, "/v1/users/oauth/access/token"),
    tokenEndpointAuthMethod: () => "client_secret_post",
    normalizeAuthorizationRequest: ({
      client_id,
      redirect_uri,
      scope,
      ...parameters
    }) => ({
      ...parameters,
      clientId: client_id!,
      redirectUri: redirect_uri!,
      scopes: scope!,
    }),
    clientCredentialsFrom: "db",
    scopes: () => ["read_write"],
    pkce: () => true,
  },
  connectionTest: {
    protocol: "http",
    request: ({ tenantUrl }) => ({
      url: endpoint(tenantUrl, "/v1/meta/services/objects/"),
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        externalUse: "true",
        parentObject: "company",
        excludeMappings: "false",
        sortByLabel: "false",
      }),
    }),
  },
  publicEnvVars: ({ tenantUrl }) => ({
    GAINSIGHT_URL: tenantUrl || "",
  }),
});
